import {
 all,
 call,
 put,
 select,
 takeEvery,
 takeLatest,
} from 'redux-saga/effects';
import { expertiseService, expertsApplyService } from '../services';
import {
 ActionPayload,
 loadedItem,
 loadedItems,
 loadingItem,
 LOADING_ITEM,
 LOADING_ITEMS,
 UPDATE_APPLICATION_STATUS,
 SEND_PUBLIC_MESSAGE,
 UPDATING_ITEM,
 addCaseNoteError,
 ADD_CASE_NOTE,
 ADD_INTERNAL_MESSAGE,
 gotUnreadMessageCount,
 GET_UNREAD_MESSAGES_COUNT,
 getUnreadMessageCount,
 UPDATE_ASSESSMENT_APPROVAL,
 UPDATE_TEACHING_APPROVAL,
 UPDATE_INDUSTRY_APPROVAL,
 getAllEvaluations,
 GET_ALL_EVALUATIONS,
 gotAllEvaluations,
 GET_MESSAGE_THREADS,
 gotMessageThreads,
 MARK_MESSAGES_AS_READ,
 UPDATE_EXPERTISE
} from './actions';
import { authSelector } from './selectors';
import {
 ExpertsApplyModel,
 Recipient,
} from '../domain/ExpertsApplyFormModel';

import {
 PagedResponse,
 PatchWithMeta,
 QueryParams,
 ValidatedResponse,
} from '@ofqual/portal-core/core/types';
import { refreshActivityFeed } from '@ofqual/portal-activity-feed';
import { QualificationModel } from '../domain/QualificationModel';
import { ActivityType } from '../domain/ActivityType';
import { ApprovalSubject, AssessmentEvaluation, Evaluation, IndustryEvaluation, TeachingEvaluation } from '../domain/ApprovalSubject';
import { ChangeApplicationStatusModel } from '../presentation/types';
import { MessageThread } from '@ofqual/portal-activity-feed/activityfeed/domain/PublicMessageModel';
import { SubjectModel } from '../domain/SubjectModel';

function* watchItemSaga() {
 yield takeLatest(LOADING_ITEM, itemSaga);
}

function* watchItemsSaga() {
 yield takeLatest(LOADING_ITEMS, itemsSaga);
}

function* watchUpdateApplicationStatusSaga() {
 yield takeLatest(UPDATE_APPLICATION_STATUS, updateApplicationStatusSaga);
}

function* watchSendPublicMessage() {
 yield takeLatest(SEND_PUBLIC_MESSAGE, sendPublicMessage);
}

function* watchGetMessageThreads()
{
  yield takeLatest(GET_MESSAGE_THREADS, getMessageThreads);
}

function* watchGetUnreadMessageThreadsSaga() {
 yield takeLatest(GET_UNREAD_MESSAGES_COUNT, getUnreadMessagesCountSaga);
}

function* watchMarkMessageAsReadSaga() {
  yield takeLatest(MARK_MESSAGES_AS_READ, markMessageAsReadSaga);
}

function* watchGetAllEvaluations() {
 yield takeLatest(GET_ALL_EVALUATIONS, getAllEvaluationsSaga);
}

function* watchUpdateItemSaga() {
 yield takeEvery(UPDATING_ITEM, updateItemSaga);
}

function* watchAddCaseNoteSaga() {
 yield takeEvery(ADD_CASE_NOTE, addCaseNoteSaga);
}

function* watchAddInternalMessage() {
 yield takeLatest(ADD_INTERNAL_MESSAGE, addInternalMessage);
}

function* watchUpdateAssessmentApprovalSaga() {
 yield takeLatest(UPDATE_ASSESSMENT_APPROVAL, updateAssessmentApprovalSaga);
}

function* watchUpdateTeachingApprovalSaga() {
 yield takeLatest(UPDATE_TEACHING_APPROVAL, updateTeachingApprovalSaga);
}

function* watchUpdateIndustryApprovalSaga() {
 yield takeLatest(UPDATE_INDUSTRY_APPROVAL, updateIndustryApprovalSaga);
}

function* watchUpdateExpertiseSaga()
{
  yield takeLatest(UPDATE_EXPERTISE, updateExpertiseSaga);
}

function* itemSaga({ payload }: ActionPayload<string>) {
 const auth: ReturnType<typeof authSelector> = yield select(authSelector);

 const item: ExpertsApplyModel = yield call(
  [expertsApplyService, expertsApplyService.get],
  payload,
  {
   token: auth.token,
  },
 );
 yield put(loadedItem(item));
 yield put(refreshActivityFeed(item.id));
 yield put(getUnreadMessageCount(item.id));
 yield put(getAllEvaluations(item.id));
}

function* itemsSaga({ payload }: ActionPayload<QueryParams>) {
 const auth: ReturnType<typeof authSelector> = yield select(authSelector);

 const items: PagedResponse<ExpertsApplyModel> = yield call(
  [expertsApplyService, expertsApplyService.search],
  {
   ...payload,
  },
  {
   token: auth.token,
  },
 );
 yield put(loadedItems(items));
}

function* updateApplicationStatusSaga({ payload }: ActionPayload<any>) {
 const auth: ReturnType<typeof authSelector> = yield select(authSelector);

 const { id, applicationStatus } = payload as {
  id: string;
  applicationStatus: ChangeApplicationStatusModel;
 };

 const validatedResponse: ValidatedResponse<ExpertsApplyModel> = yield call(
  [expertsApplyService, expertsApplyService.updateApplicationStatus],
  id,
  applicationStatus,
  {
   token: auth.token,
  },
 );

 if (validatedResponse.isValid) {
  yield put(refreshActivityFeed(id));
  yield put(loadingItem(id));
 }
}

function* sendPublicMessage({
 payload,
}: ActionPayload<{
 id: string;
 files: File[];
 subject: string;
 message: string;
 threadId: string;
}>) {
 const { token } = yield select(authSelector);
 const { id, files, subject, message, threadId } = payload;
 try {
  yield call(
   [expertsApplyService, expertsApplyService.sendPublicMessage],
   id,
   files,
   subject,
   message,
   threadId,
   {
    token,
   },
  );
  yield put(refreshActivityFeed(id));
 } catch (err: any) {
  throw err;
 }
}

function* getUnreadMessagesCountSaga({
 payload,
}: ActionPayload<{
 citizenId: string;
}>) {
 const { token } = yield select(authSelector);
 const { citizenId } = payload;
 try {
  const count: number = yield call(
   [expertsApplyService, expertsApplyService.countUnreadMessages],
   citizenId,
   ActivityType.SpecialistCitizenSendPublicMessage,
   { token },
  );
  yield put(gotUnreadMessageCount(count));
 } catch (err: any) {
  throw err;
 }
}

function* addInternalMessage({
 payload,
}: ActionPayload<{
 id: string;
 files: File[];
 subject: string;
 message: string;
 recipients: Recipient[];
}>) {
 const { token } = yield select(authSelector);
 const { id, files, subject, message, recipients } = payload;

 try {
  yield call(
   [expertsApplyService, expertsApplyService.addInternalMessage],
   id,
   files,
   subject,
   message,
   recipients,
   {
    token,
   },
  );
  yield put(refreshActivityFeed(id));
 } catch (err: any) {
  if (err?.errorCode === 0) {
   yield put(addCaseNoteError("This enquiry has been closed"));
  } else {
   throw err;
  }
 }
}

function* updateItemSaga({ payload }: ActionPayload<any>) {
 const { token } = yield select(authSelector);

 const { id, qualifications, patchDocument } = payload as {
  id: string;
  qualifications: QualificationModel[];
  patchDocument: PatchWithMeta<ExpertsApplyModel>;
 };

 const attachmentsCall = () => {
  try {
   const addedQualsWithFiles = qualifications.filter(
    (q) => q.uploadFile && q.uploadFile.length > 0,
   );

   if (addedQualsWithFiles !== undefined && addedQualsWithFiles.length > 0) {
    const files: File[] = [];
    addedQualsWithFiles.forEach((q) => {
     if (q.uploadFile !== undefined) {
      if (q.uploadFile.length > 0) {
       files.push(q.uploadFile[0]);
      }
     }
    });
    return expertsApplyService.postAttachments(id, addedQualsWithFiles, files, {
     token: token,
    });
   } else {
    return Promise.resolve();
   }
  } catch (err: any) {
   Promise.reject(err);
  }
 };

 yield all({
  validatedResponse: call(
   [expertsApplyService, expertsApplyService.update],
   id,
   patchDocument,
   {
    token,
   },
  ),
  newDocs: call(attachmentsCall),
 });

  yield put(loadingItem(id));
}

function* addCaseNoteSaga({
 payload,
}: ActionPayload<{
 id: string;
 caseFiles: File[];
 caseNote: string;
 sla_ResponseType: number;
 subject: string;
}>) {
 const { token } = yield select(authSelector);
 const { id, caseFiles, caseNote, subject } = payload;
 try {
  yield call(
   [expertsApplyService, expertsApplyService.addCaseNote],
   id,
   caseFiles,
   caseNote,
   subject,
   {
    token,
   },
  );
  yield put(refreshActivityFeed(id));
 } catch (err: any) {
  if (err?.errorCode === 0) {
   yield put(addCaseNoteError('This enquiry has been closed'));
  } else {
   throw err;
  }
 }
}

function* updateAssessmentApprovalSaga({
 payload,
}: ActionPayload<{
 id: string;
 approvalSubjects: ApprovalSubject[];
 assessmentEvaluation: AssessmentEvaluation | undefined;
 send: boolean
}>) {
 const { token } = yield select(authSelector);
 const { id, approvalSubjects, assessmentEvaluation, send } = payload;
 try {
  yield call(
   [expertsApplyService, expertsApplyService.updateAssessmentApprovals],
   id,
   approvalSubjects,
   assessmentEvaluation,
   send,
   {
    token,
   },
  );
  yield put(getAllEvaluations(id));
 } catch (err: any) {
  throw err;
 }
}

function* updateTeachingApprovalSaga({
 payload,
}: ActionPayload<{
 id: string;
 approvalSubjects: ApprovalSubject[];
 teachingEvaluation: TeachingEvaluation | undefined;
 send: boolean;
}>) {
 const { token } = yield select(authSelector);
 const { id, approvalSubjects, teachingEvaluation, send } = payload;
 try {
  yield call(
   [expertsApplyService, expertsApplyService.updateTeachingApprovals],
   id,
   approvalSubjects,
   teachingEvaluation,
   send,
   {
    token,
   },
  );
  yield put(getAllEvaluations(id));
 } catch (err: any) {
  throw err;
 }
}

function* updateIndustryApprovalSaga({
 payload,
}: ActionPayload<{
 id: string;
 approvalSubjects: ApprovalSubject[];
 industryEvaluation: IndustryEvaluation | undefined;
 send: boolean;
}>) {
 const { token } = yield select(authSelector);
 const { id, approvalSubjects, industryEvaluation, send } = payload;
 try {
  yield call(
   [expertsApplyService, expertsApplyService.updateIndustryApprovals],
   id,
   approvalSubjects,
   industryEvaluation,
   send,
   {
    token,
   },
  );
  yield put(getAllEvaluations(id));
 } catch (err: any) {
  throw err;
 }
}

function* getAllEvaluationsSaga({
 payload,
}: ActionPayload<{
 citizenId: string;
}>) {
 const { token } = yield select(authSelector);
 const { citizenId } = payload;
 try {
  const evaluation: Evaluation = yield call(
   [expertsApplyService, expertsApplyService.getAllEvaluations],
   citizenId,
   { token },
  );
  yield put(gotAllEvaluations(evaluation))
 } catch (err: any) {
  throw err;
 }
}

function* getMessageThreads({
  payload,
}: ActionPayload<{
  citizenId: string;
}>) {
  const { token } = yield select(authSelector);
  const { citizenId } = payload;

  try {
    const messageThreads: MessageThread[] = yield call(
      [expertsApplyService, expertsApplyService.getMessageThreads],
      citizenId,
      { token }
    );
    yield put(gotMessageThreads(messageThreads))   


  }
  catch (err: any) { throw err; }
}

function* markMessageAsReadSaga({
  payload,
}: ActionPayload<{
  messageThread: MessageThread;
}>) {
  const { token } = yield select(authSelector);
  const { messageThread } = payload;

  try {
    if (messageThread.unreadCitizenMessagesCount > 0) {
      for (let message of messageThread.messages) {
        if (
          message.msgRead === false &&
          message.activityType ===
            ActivityType.SpecialistCitizenSendPublicMessage
        ) {
          yield call(
            [expertsApplyService, expertsApplyService.sendPublicMessageRead],
            message.id,
            { token }
          );
        }
      }
    }
  } catch (err: any) {
    throw err;
  }
}

function* updateExpertiseSaga({ payload }: ActionPayload<any>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const { id, expertise } = payload as { id: string; expertise : SubjectModel};
  const validatedResponse: ValidatedResponse<SubjectModel> = yield call(
    [expertiseService, expertiseService.addExpertise],
    expertise,
    {
      token: auth.token,
    },
  );

  if (validatedResponse.isValid) {
    yield put(refreshActivityFeed(id));
    yield put(loadingItem(id));
  }
}


export function* rootSaga() {
 yield all([
   watchItemSaga(),
   watchItemsSaga(),
   watchUpdateApplicationStatusSaga(),
   watchSendPublicMessage(),
   watchUpdateItemSaga(),
   watchAddCaseNoteSaga(),
   watchAddInternalMessage(),
   watchGetUnreadMessageThreadsSaga(),
   watchUpdateAssessmentApprovalSaga(),
   watchUpdateTeachingApprovalSaga(),
   watchUpdateIndustryApprovalSaga(),
   watchGetAllEvaluations(),
   watchGetMessageThreads(),
   watchMarkMessageAsReadSaga(),
   watchUpdateExpertiseSaga()
 ]);
}
